import axios from 'axios';

import { createAuthHeaders } from '../api-client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface GitLabConnection {
  id: string;
  workspaceId: string;
  type: string;
  name: string;
  status: string;
  metadata?: {
    id: number;
    username: string;
    name: string;
    avatar_url: string;
    email?: string;
    groups?: Array<{
      id: number;
      full_path: string;
      name: string;
      avatar_url: string;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

export interface GitLabGroup {
  id: number;
  name: string;
  full_path: string;
  avatar_url: string;
  description?: string;
  web_url: string;
  parent_id?: number;
}

export interface GitLabProject {
  id: number;
  name: string;
  path: string;
  path_with_namespace: string;
  visibility: 'private' | 'internal' | 'public';
  http_url_to_repo: string;
  web_url: string;
  description?: string;
  default_branch: string;
  namespace: {
    id: number;
    name: string;
    path: string;
    full_path: string;
    kind: 'user' | 'group';
    avatar_url?: string;
  };
}

export interface GitLabBranch {
  name: string;
  commit: {
    id: string;
  };
  protected: boolean;
}

export interface GitLabConnectProjectDto {
  connectionId: string;
  owner: string;
  repoName: string;
  branch?: string;
  visibility?: 'private' | 'internal' | 'public';
  createRepo?: boolean;
  namespaceId?: string;
}

export interface GitLabImportDto {
  connectionId: string;
  owner: string;
  repoName: string;
  branch?: string;
  projectName?: string;
}

export interface GitLabConnectProjectResponse {
  id: string;
  gitlabRepoUrl: string;
  gitlabRepoOwner: string;
  gitlabRepoName: string;
  gitlabDefaultBranch: string;
  gitlabSyncEnabled: boolean;
  gitlabProjectId: number;
}

export interface GitLabImportResponse {
  id: string;
  name: string;
  workspaceId: string;
}

export const gitlabApi = {
  // OAuth
  async initiateOAuth(
    token: string,
    connectionName?: string,
    projectId?: string,
    returnPath?: string,
  ): Promise<{ authUrl: string }> {
    const response = await axios.post(
      `${API_URL}/api/gitlab/oauth/initiate`,
      { connectionName, projectId, returnPath },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  // Connections
  async listConnections(token: string): Promise<GitLabConnection[]> {
    const response = await axios.get(`${API_URL}/api/gitlab/connections`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async getConnection(token: string, connectionId: string): Promise<GitLabConnection> {
    const response = await axios.get(
      `${API_URL}/api/gitlab/connections/${connectionId}`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async deleteConnection(token: string, connectionId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/gitlab/connections/${connectionId}`, {
      headers: createAuthHeaders(token),
    });
  },

  // GitLab Data
  async listGroups(token: string, connectionId: string): Promise<GitLabGroup[]> {
    const response = await axios.get(
      `${API_URL}/api/gitlab/connections/${connectionId}/groups`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async listRepositories(
    token: string,
    connectionId: string,
    owner?: string,
    page?: number,
    perPage?: number,
    search?: string,
  ): Promise<GitLabProject[]> {
    const params = new URLSearchParams();
    if (owner) params.append('owner', owner);
    if (page) params.append('page', String(page));
    if (perPage) params.append('per_page', String(perPage));
    if (search) params.append('search', search);

    const response = await axios.get(
      `${API_URL}/api/gitlab/connections/${connectionId}/repos?${params.toString()}`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async listBranches(
    token: string,
    connectionId: string,
    owner: string,
    repo: string,
  ): Promise<GitLabBranch[]> {
    const response = await axios.get(
      `${API_URL}/api/gitlab/connections/${connectionId}/branches?owner=${owner}&repo=${repo}`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  // Project Integration
  async connectProject(
    token: string,
    projectId: string,
    dto: GitLabConnectProjectDto,
  ): Promise<GitLabConnectProjectResponse> {
    const response = await axios.post(
      `${API_URL}/api/gitlab/projects/${projectId}/connect`,
      dto,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async disconnectProject(
    token: string,
    projectId: string,
  ): Promise<{ id: string; gitlabSyncEnabled: boolean }> {
    const response = await axios.post(
      `${API_URL}/api/gitlab/projects/${projectId}/disconnect`,
      {},
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async pullFromGitlab(
    token: string,
    projectId: string,
  ): Promise<{ success: boolean; error?: string }> {
    const response = await axios.post(
      `${API_URL}/api/gitlab/projects/${projectId}/pull`,
      {},
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async importRepo(
    token: string,
    dto: GitLabImportDto,
  ): Promise<GitLabImportResponse> {
    const response = await axios.post(
      `${API_URL}/api/gitlab/import`,
      dto,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },
};
